/**
 * DTO Conversion Stage
 *
 * Converts API DTOs to raw domain telemetry data
 */

import {
  PipelineStage,
  PipelineContext,
  StageResult,
} from '../ProcessingPipeline'
import {
  RawTelemetryData,
  DeviceMetadata,
} from '../../../domain/telemetry/raw/RawTelemetryData'
import { GpsReading } from '../../../domain/telemetry/raw/GpsReading'
import { GyroscopeReading } from '../../../domain/telemetry/raw/GyroscopeReading'
import { AccelerometerReading } from '../../../domain/telemetry/raw/AccelerometerReading'
import {
  TelemetryDto,
  LatLngDto,
  GyroReading,
  AccelerationReading,
} from '../../../api/apiTypes'
import { messageOf } from '../../../utils/errorMessage'

export class DtoConversionStage implements PipelineStage {
  name = 'DTO Conversion'

  async execute(context: PipelineContext): Promise<StageResult<void>> {
    const warnings: string[] = []

    try {
      const { dto } = context
      const telemetryDto = dto.telemetry

      if (!telemetryDto) {
        return {
          success: false,
          error: 'No telemetry data in DTO',
        }
      }

      // Convert GPS readings
      const gpsReadings = this.convertGpsReadings(telemetryDto)
      if (gpsReadings.length === 0) {
        return {
          success: false,
          error: 'No GPS data available',
        }
      }

      // Convert gyroscope readings
      const gyroscopeReadings = this.convertGyroscopeReadings(telemetryDto)
      if (!gyroscopeReadings || gyroscopeReadings.length === 0) {
        warnings.push(
          'No gyroscope data available - lean angle will be less accurate'
        )
      }

      // Convert accelerometer readings
      const accelerometerReadings =
        this.convertAccelerometerReadings(telemetryDto)
      if (!accelerometerReadings || accelerometerReadings.length === 0) {
        warnings.push(
          'No accelerometer data available - acceleration will be calculated from GPS'
        )
      }

      // Build device metadata
      const deviceMetadata: DeviceMetadata | undefined = telemetryDto.device
        ? {
            deviceName: telemetryDto.device.name,
            deviceVersion: telemetryDto.device.version,
          }
        : undefined

      // Get recording start time (handle null metadata)
      const recordingStartTime = dto.metadata?.sessionStartTime
        ? this.toDate(dto.metadata.sessionStartTime)
        : gpsReadings.length > 0
          ? gpsReadings[0].timestamp
          : new Date()

      // Create raw telemetry data
      const rawTelemetry: RawTelemetryData = {
        gps: gpsReadings,
        gyroscope: gyroscopeReadings,
        accelerometer: accelerometerReadings,
        device: deviceMetadata,
        recordingStartTime,
      }

      context.rawTelemetry = rawTelemetry

      return {
        success: true,
        warnings: warnings.length > 0 ? warnings : undefined,
      }
    } catch (error) {
      return {
        success: false,
        error: `DTO conversion failed: ${messageOf(error)}`,
      }
    }
  }

  /**
   * Convert GPS DTO readings to domain GPS readings
   */
  private convertGpsReadings(dto: TelemetryDto): GpsReading[] {
    if (!dto.gps?.positions) {
      return []
    }

    return dto.gps.positions.map((pos: LatLngDto) => ({
      latitude: pos.latitude,
      longitude: pos.longitude,
      altitude: pos.altitude,
      speedMs: pos.speed.kph / 3.6, // Convert km/h to m/s
      timestamp: this.toDate(pos.time.recordedTime),
      accuracy: undefined, // Not available in DTO
    }))
  }

  /**
   * Convert gyroscope DTO readings to domain gyroscope readings
   */
  private convertGyroscopeReadings(
    dto: TelemetryDto
  ): GyroscopeReading[] | undefined {
    if (!dto.gyroscope?.gyroReading) {
      return undefined
    }

    const unit = (dto.gyroscope?.unit === 'deg/s' ? 'deg/s' : 'rad/s') as
      | 'rad/s'
      | 'deg/s'

    return dto.gyroscope.gyroReading.map((reading: GyroReading) => ({
      x: reading.x,
      y: reading.y,
      z: reading.z,
      timestamp: this.toDate(reading.time.recordedTime),
      unit,
    }))
  }

  /**
   * Convert accelerometer DTO readings to domain accelerometer readings
   */
  private convertAccelerometerReadings(
    dto: TelemetryDto
  ): AccelerometerReading[] | undefined {
    if (!dto.accelerometer?.accelerationReading) {
      return undefined
    }

    const unit = (dto.accelerometer?.unit === 'm/s²' ? 'm/s²' : 'g') as
      | 'g'
      | 'm/s²'

    return dto.accelerometer.accelerationReading.map(
      (reading: AccelerationReading) => ({
        x: reading.x,
        y: reading.y,
        z: reading.z,
        timestamp: this.toDate(reading.time.recordedTime),
        unit,
      })
    )
  }

  /**
   * Convert a value to Date - handles both Date objects and ISO strings
   */
  private toDate(value: Date | string): Date {
    if (value instanceof Date) {
      return value
    }
    return new Date(value)
  }
}
